import { CalendarDays } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Calendar } from 'components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import ConfigurationHeader from 'components/configuration/ConfigurationHeader';
import { Presentation } from 'iconoir-react';
import { formatRangeLabel, formatWindow, getUserLabel } from 'features/usage/utils/usage-format';

export function UsageHeader({
  periodStart,
  periodEnd,
  selectedRange,
  initialFrom,
  today,
  viewingUserId,
  viewingUserName,
  dayCount,
  onRangeSelect,
  onResetRange,
  onBack,
}: {
  periodStart?: string;
  periodEnd?: string;
  selectedRange: DateRange | undefined;
  initialFrom?: Date;
  today: Date;
  viewingUserId?: string;
  viewingUserName?: string;
  dayCount: number;
  onRangeSelect: (range: DateRange | undefined) => void;
  onResetRange: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const selectRangeLabel = t('SELECT_RANGE');

  return (
    <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      <div>
        <div className="flex items-center gap-2">
          <ConfigurationHeader
            title={t('USAGE') || 'Usage'}
            titleClassName="font-semibold"
            icon={
              <Presentation
                width={20}
                height={20}
                strokeWidth={1.75}
                className="text-[#3d3d3d] dark:text-foreground"
              />
            }
          />
          <span className="inline-flex items-center rounded-md border border-border bg-muted px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t('BETA')}
          </span>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          {periodStart && periodEnd
            ? formatWindow(periodStart, periodEnd)
            : formatRangeLabel(selectedRange, selectRangeLabel)}
        </p>
        {viewingUserId && (
          <div className="mt-3 flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-muted-foreground"
              onClick={onBack}
            >
              {t('BACK')}
            </Button>
            <Badge variant="outline" className="w-fit rounded-md px-3 py-1 font-mono text-xs">
              {viewingUserName || getUserLabel(viewingUserId)}
            </Badge>
          </div>
        )}
      </div>

      <div className="flex flex-col items-start gap-3 md:items-end">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="h-auto min-w-[240px] justify-between rounded-xl border-border bg-card px-4 py-2.5 text-left shadow-none"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-25 text-primary">
                  <CalendarDays className="h-4 w-4" />
                </span>
                <div>
                  <div className="text-xs font-medium text-muted-foreground">{t('DATE_RANGE')}</div>
                  <div className="font-mono text-xs text-foreground">
                    {formatRangeLabel(selectedRange, selectRangeLabel)}
                  </div>
                </div>
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto rounded-xl border-border p-0 shadow-lg">
            <div className="border-b border-border px-4 py-3">
              <div className="text-xs font-medium text-muted-foreground">{t('FILTER_PERIOD')}</div>
              <div className="mt-1 text-sm text-foreground">
                {periodStart && periodEnd
                  ? formatWindow(periodStart, periodEnd)
                  : formatRangeLabel(selectedRange, selectRangeLabel)}
              </div>
            </div>
            <Calendar
              mode="range"
              numberOfMonths={2}
              defaultMonth={selectedRange?.from ?? initialFrom}
              selected={selectedRange}
              onSelect={onRangeSelect}
              disabled={(date) => date > today}
              initialFocus
              className="p-3"
            />
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={onResetRange}>
                {t('RESET')}
              </Button>
              <div className="font-mono text-xs text-muted-foreground">
                {dayCount === 1
                  ? t('DAY_COUNT', { count: periodStart ? dayCount : 0 })
                  : t('DAYS_COUNT', { count: periodStart ? dayCount : 0 })}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </header>
  );
}
